import styled, {css, keyframes} from "styled-components";
import BaseButton from "components/BaseButton";
import {FlowColumn} from "home/HomePage.styles";
import {CampaignSurfaceRoot} from "home/landing/CampaignSurface.styles";
import {CAMPAIGN_SIDE_BY_SIDE_MIN_WIDTH_PX} from "home/landing/campaignSpace";
import {Backdrop, Card, FlowRoot} from "home/CreateFlow.styles";

export const ReplyFrame = styled.div<{$replyAvailable: boolean}>`
	/* The compact reply card, in the stacked layout's 404px column only: beside the campaign the column
	   is already createCard.sideBySideWidthPx (360px), narrower than this (kitecorp/transferify-ui#2888). */
	${({$replyAvailable}) => $replyAvailable && css`
		@media (max-width: ${CAMPAIGN_SIDE_BY_SIDE_MIN_WIDTH_PX - 1}px) { --tf-recipient-card-w: 380px; }
	`}
	min-height: 0; max-height: 100%; width: 100%; display: flex; flex-direction: column;
	> [data-peel-frame] { min-height: 0; }
	/* min-height, not overflow hidden: the card scrolls internally already, and clipping here cut the
	   card's shadow and edge the instant the prompt mounted mid-retreat (owner 2026-09-10). */
	&:has([data-reply-prompt]) [data-peel-card] { flex: 1 1 auto; min-height: 0; }
	[data-peel-action-position] { flex: none; }
`;
const replyIconNudge = keyframes`
	0%, 100% { transform: translateX(0) rotate(0); }
	45% { transform: translateX(-4px) rotate(-10deg); }
`;
/** Only the return icon moves; the control and card remain still. */
export const ReplyPull = styled(BaseButton)`
	&& { border-radius: 8px; transition: none; }
	gap: 7px;
	> svg { width: 17px; height: 17px; flex: none; }
	&:hover > svg, &:focus-visible > svg { animation: ${replyIconNudge} .55s ease-out; }
	@media (prefers-reduced-motion: reduce) {
		&:hover > svg, &:focus-visible > svg { animation: none; }
	}
`;
export const ReplyButton = styled.button`
	min-height: 44px; padding: 8px 14px; font: inherit; font-weight: 600;
	border-radius: 10px; border: 1px solid ${({theme}) => theme.tokens.cardBorder};
	background: ${({theme}) => theme.tokens.card}; color: ${({theme}) => theme.tokens.ink}; cursor: pointer;
	&:focus-visible { outline: 2px solid ${({theme}) => theme.tokens.focusOutline}; outline-offset: 2px; }
`;
/** The same landing tree changes geometry, never React owner or portal container. */
export const ComposerCanvas = styled.div<{$home: boolean; $open: boolean}>`
	display: ${({$home, $open}) => $home || $open ? "contents" : "none"};
	${({$home}) => !$home && css`
		> ${CampaignSurfaceRoot} { position: fixed; inset: 0; background: transparent; overflow: visible; pointer-events: none; z-index: 20; }
	`}
`;
/* The panel covers the recipient card by sliding in from its right (owner 2026-09-10), rather than
   appearing in place: the motion says "this sits ON TOP of the card you were just reading".
   Closing plays the same path backwards; HomePage holds the panel mounted for REPLY_SLIDE_MS so the
   exit has something to animate, and skips that hold entirely under reduced motion. */
export const REPLY_SLIDE_MS = 380;
const replySlideIn = keyframes`
	from { transform: translateX(calc(100% + 24px)); opacity: 0; }
	to { transform: translateX(0); opacity: 1; }
`;
const replySlideOut = keyframes`
	from { transform: translateX(0); opacity: 1; }
	to { transform: translateX(calc(100% + 24px)); opacity: 0; }
`;
export const ReplyFlowColumn = styled(FlowColumn)<{$reply: boolean; $closing?: boolean}>`
	${({$reply}) => $reply && css`
		position: fixed; left: var(--reply-left); top: var(--reply-top);
		width: var(--reply-width); height: var(--reply-height); max-height: var(--reply-height);
		margin: 0; visibility: visible; pointer-events: auto; overflow: hidden; border-radius: 22px;
		animation: ${replySlideIn} ${REPLY_SLIDE_MS}ms cubic-bezier(.2,.8,.3,1) both;
		@media (prefers-reduced-motion: reduce) { animation: none; }
	`}
	${({$closing}) => $closing && css`
		pointer-events: none;
		animation-name: ${replySlideOut};
	`}
`;
export const ReplyPanel = styled.div<{$reply: boolean}>`
	width: 100%; min-height: 0;
	${({$reply, theme}) => $reply && `
		height: 100%; overflow-y: auto; background: ${theme.tokens.card}; color: ${theme.tokens.ink};
		border: 1px solid ${theme.tokens.cardBorder}; border-radius: 22px;
	`}
	${Backdrop} { ${({$reply}) => $reply && "padding: 0; min-height: 0;"} }
	${FlowRoot} { ${({$reply}) => $reply && "height: auto;"} }
	${Card} { ${({$reply}) => $reply && "box-shadow: none; border: none; max-height: none;"} }
`;
export const ReplyHeader = styled.header`
	position: sticky; top: 0; z-index: 5; padding: 8px 12px 8px 20px;
	display: flex; align-items: center; justify-content: space-between; gap: 8px;
	background: ${({theme}) => theme.tokens.card};
	h2 { margin: 0; font-size: 20px; }
	${ReplyButton} { flex: none; width: 44px; height: 44px; padding: 0; font-size: 22px; }
`;
